import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import type { PersistStorage, StorageValue } from 'zustand/middleware';
import { persist } from 'zustand/middleware';

import { getActionTitleByType, getSuccessToastDescription, getSuccessToastTitle } from '@/components/drawers/helpers';
import { showToast } from '@/components/Toast';
import { getConfig } from '@/config/config';
import { getFrontendNetwork } from '@/config/deployment';
import type { TransactionHistoryItem } from '@/context/types';
import { scheduleVaultDataInvalidation, scheduleWalletDataInvalidation } from '@/hooks';
import { pollTxStatusAndSync } from '@/hooks/api/api.helpers';
import type { VaultTransaction } from '@/hooks/api/types';
import { fetchTransactionStatus } from '@/hooks/api/usePollTransactionStatus';
import {
  applyTransactionTombstones,
  clearStaleTransactionState,
  isSwapNotification,
  notificationRecordKey,
  notificationStorageKey,
  parseStoredNotifications,
} from '@/lib/resetPersistedTransactionNotifications';
import type { NotificationInput, NotificationItem } from '@/types/notification.types';

import {
  hasPendingSwapAction as hasPendingSwapActionInList,
  hasPendingVaultAction as hasPendingVaultActionInList,
  hasSwapUserActionRequired as hasSwapUserActionRequiredInList,
} from './notification.helpers';

export type TransactionRecoveryResult = {
  dropped: number;
  confirmed: number;
  pending: number;
  tooRecent: number;
  failed: number;
};

interface NotificationState {
  clearedNotificationIds: string[];
  clearedTransactionIds: string[];
  vaultPubkey: string | null;
  runeAddress: string | null;
  notifications: NotificationItem[];
  isInitialized: boolean;
  // Runtime wallet policy; never restore this permission from local storage.
  allowVaultHistory: boolean;
  /*
    Pending transaction depending on type affects the following sources (Vault, Wallet BTC, Wallet Unit):
    - Open: V-W
    - Deposit: V-W
    - Withdraw: V-W
    - Borrow: V-W-U
    - Repay: V-W-U
    - Repossess: V-W
    - Swap: W-U
    - Faucet: W
  */
  pendingTransaction: TransactionHistoryItem | undefined;
  pendingTransactions: TransactionHistoryItem[];

  initialize: (vaultPubkey: string, runeAddress: string, options?: { allowVaultHistory: boolean }) => void;
  reset: () => void;
  addNotification: (notification: NotificationInput) => string;
  updateNotification: (id: string, updates: Partial<NotificationInput>) => void;
  removeNotification: (id: string) => void;
  clearTransactionState: () => void;
  reportDroppedTransactions: (canRecover?: () => boolean) => Promise<TransactionRecoveryResult>;
  markAsViewed: (id: string, toggle?: boolean) => void;
  markAllAsViewed: () => void;
  clearAll: () => void;
  getUnviewedCount: () => number;
  /** @deprecated Prefer hasPendingVaultAction — vault in-progress only (excludes UNIT→USDC swap). */
  hasInProgress: () => boolean;
  hasPendingVaultAction: () => boolean;
  hasPendingSwapAction: () => boolean;
  hasSwapUserActionRequired: () => boolean;
  setPendingTransaction: (
    item: TransactionHistoryItem,
    options?: {
      shouldPollStatus?: boolean;
      shouldShowToast?: boolean;
      onTxInMempool?: () => void;
      onConfirmed?: (tx: TransactionHistoryItem) => void;
    },
  ) => void;
  checkPendingNotifications: (transactions: VaultTransaction[]) => void;
}

const getStorageKey = notificationStorageKey;

// Module-level reference to current vault pubkey for custom storage
let currentVaultPubkey: string | null = null;

// Each transaction retains its own callbacks; another tab must not replace them.
const activePolls = new Map<string, AbortController>();
const confirmedTransactions = new Set<string>();
let walletSession = 0;
const abortPolls = () => {
  walletSession += 1;
  for (const controller of activePolls.values()) controller.abort();
  activePolls.clear();
  confirmedTransactions.clear();
};

type PersistedState = Pick<
  NotificationState,
  | 'notifications'
  | 'vaultPubkey'
  | 'runeAddress'
  | 'isInitialized'
  | 'pendingTransactions'
  | 'pendingTransaction'
  | 'clearedNotificationIds'
  | 'clearedTransactionIds'
>;

// Ordinary notification updates must keep working when browser storage is blocked.
// The persistence adapter separately refuses writes if its latest read fails;
// explicit Recovery intentionally uses a strict read before changing any state.
function readStoredNotifications(vaultPubkey: string | null) {
  try {
    return vaultPubkey && typeof window !== 'undefined'
      ? parseStoredNotifications(window.localStorage.getItem(getStorageKey(vaultPubkey)))?.state
      : undefined;
  } catch {
    return undefined;
  }
}

// Custom storage that uses dynamic key based on vault pubkey
const createVaultStorage = (): PersistStorage<PersistedState> => ({
  getItem: () => {
    if (globalThis.window === undefined || !currentVaultPubkey) {
      return null;
    }

    try {
      const key = getStorageKey(currentVaultPubkey);
      const value = globalThis.window.localStorage.getItem(key);

      if (!value) return null;
      const parsed = parseStoredNotifications(value);
      if (!parsed || (parsed.state.vaultPubkey && parsed.state.vaultPubkey !== currentVaultPubkey)) return null;
      const state = applyTransactionTombstones(parsed.state);
      return {
        ...parsed,
        state: { ...state, pendingTransaction: state.pendingTransaction?.status === 'In Progress' ? state.pendingTransaction : undefined },
      } as StorageValue<PersistedState>;
    } catch {
      return null;
    }
  },
  setItem: (_, value) => {
    if (globalThis.window === undefined || !currentVaultPubkey) {
      return;
    }

    try {
      const key = getStorageKey(currentVaultPubkey);

      const raw = globalThis.window.localStorage.getItem(key);
      const persisted = parseStoredNotifications(raw)?.state;
      globalThis.window.localStorage.setItem(key, JSON.stringify({ ...value, state: applyTransactionTombstones(value.state, persisted) }));
    } catch {}
  },
  removeItem: () => {
    if (globalThis.window === undefined || !currentVaultPubkey) {
      return;
    }

    try {
      const key = getStorageKey(currentVaultPubkey);
      globalThis.window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
});

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      clearedNotificationIds: [],
      clearedTransactionIds: [],
      vaultPubkey: null,
      runeAddress: null,
      notifications: [],
      isInitialized: false,
      allowVaultHistory: false,
      pendingTransaction: undefined,
      pendingTransactions: [],

      initialize: (vaultPubkey: string, runeAddress: string, { allowVaultHistory } = { allowVaultHistory: true }) => {
        // Update module-level reference for storage
        if (currentVaultPubkey !== vaultPubkey) abortPolls();
        currentVaultPubkey = vaultPubkey;

        // Load from storage for this vault
        let notifications: NotificationItem[] = [];
        let pendingTransaction: TransactionHistoryItem | undefined;
        let pendingTransactions: TransactionHistoryItem[] = [];
        let clearedNotificationIds: string[] = [];
        let clearedTransactionIds: string[] = [];

        if (globalThis.window !== undefined) {
          try {
            const key = getStorageKey(vaultPubkey);
            const stored = globalThis.window.localStorage.getItem(key);
            if (stored) {
              const parsed = parseStoredNotifications(stored);
              if (parsed && (!parsed.state.vaultPubkey || parsed.state.vaultPubkey === vaultPubkey)) {
                const restored = applyTransactionTombstones(parsed.state);
                notifications = restored.notifications;
                pendingTransactions = restored.pendingTransactions ?? [];
                pendingTransaction = restored.pendingTransaction?.status === 'In Progress' ? restored.pendingTransaction : undefined;
                clearedNotificationIds = restored.clearedNotificationIds ?? [];
                clearedTransactionIds = restored.clearedTransactionIds ?? [];
              }
            }
          } catch {
            // ignore
          }
        }

        set({
          vaultPubkey,
          runeAddress,
          notifications,
          pendingTransaction,
          pendingTransactions,
          clearedNotificationIds,
          clearedTransactionIds,
          isInitialized: true,
          allowVaultHistory,
        });

        for (const tx of pendingTransactions) get().setPendingTransaction(tx, { shouldPollStatus: true, shouldShowToast: true });
      },

      reset: () => {
        abortPolls();

        currentVaultPubkey = null;

        set({
          clearedNotificationIds: [],
          clearedTransactionIds: [],
          vaultPubkey: null,
          runeAddress: null,
          notifications: [],
          isInitialized: false,
          allowVaultHistory: false,
          pendingTransaction: undefined,
          pendingTransactions: [],
        });
      },

      addNotification: (notification: NotificationInput) => {
        const current = get();
        const persisted = readStoredNotifications(current.vaultPubkey);
        const state = applyTransactionTombstones(current, persisted);

        if (!state.vaultPubkey) {
          return '';
        }

        const id = notification.id || uuidv4();
        const txid = notification.metadata?.txid;

        // Find existing notification by id or by metadata.txid
        const existingNotification = state.notifications.find((n) => n.id === id || (txid && n.metadata?.txid === txid));

        if (existingNotification) {
          // Update existing notification
          set({
            notifications: applyTransactionTombstones(
              {
                ...state,
                notifications: state.notifications.map((n) =>
                  n.id === existingNotification.id ? { ...n, ...notification, id: existingNotification.id, isViewed: false } : n,
                ),
              },
              state,
            ).notifications,
          });

          return existingNotification.id;
        }

        // Add new notification
        const newNotification: NotificationItem = {
          ...notification,
          id,
          instanceId: uuidv4(),
          isViewed: false,
          createdAt: Date.now(),
        };

        set({ notifications: [newNotification, ...state.notifications] });

        return id;
      },

      updateNotification: (id: string, updates: Partial<NotificationInput>) => {
        const state = get();

        if (!state.vaultPubkey) {
          return;
        }

        const persisted = readStoredNotifications(state.vaultPubkey);
        const current = applyTransactionTombstones(state, persisted);
        set({
          notifications: applyTransactionTombstones(
            { ...current, notifications: current.notifications.map((n) => (n.id === id ? { ...n, ...updates } : n)) },
            current,
          ).notifications,
        });
      },

      removeNotification: (id: string) => {
        const state = get();

        if (!state.vaultPubkey) {
          return;
        }

        const removed = state.notifications.filter((n) => n.id === id);
        set({
          notifications: state.notifications.filter((n) => n.id !== id),
          clearedNotificationIds: [...new Set([...state.clearedNotificationIds, ...removed.map(notificationRecordKey)])],
        });
      },

      clearTransactionState: () => {
        const state = get();
        const cleared = clearStaleTransactionState(applyTransactionTombstones(state, readStoredNotifications(state.vaultPubkey)));
        set(cleared);
        // Keep existing poll callbacks alive and pick up submissions from other
        // tabs, or polls that previously stopped after a network error.
        for (const tx of cleared.pendingTransactions) get().setPendingTransaction(tx, { shouldPollStatus: true, shouldShowToast: true });
      },

      reportDroppedTransactions: async (canRecover = () => true) => {
        const state = get();
        const session = walletSession;
        const current = applyTransactionTombstones(state, readStoredNotifications(state.vaultPubkey));
        const candidates = new Map<string, { date: number; actionType?: string }>();
        for (const item of current.notifications) {
          if (item.type !== 'progress' || !item.metadata?.txid || isSwapNotification(item)) continue;
          candidates.set(item.metadata.txid, { date: item.createdAt, actionType: item.metadata.actionType });
        }
        for (const tx of current.pendingTransactions) {
          // Resumable swaps keep their own recovery protocol.
          if (tx.type === 'swap') continue;
          candidates.set(tx.transactionId, { date: Date.parse(tx.date), actionType: tx.type });
        }
        const result: TransactionRecoveryResult = { dropped: 0, confirmed: 0, pending: 0, tooRecent: 0, failed: 0 };
        const config = getConfig(getFrontendNetwork());
        const isCurrent = () => walletSession === session && get().vaultPubkey === state.vaultPubkey;
        // Take fresh reads before changing any records. HTTP failures and timeouts
        // are not evidence that a transaction was dropped.
        const checks = await Promise.all(
          [...candidates].map(async ([txid, candidate]) => {
            try {
              if (!config?.esploraUrl) return { txid, candidate, outcome: 'failed' as const };
              const status = await fetchTransactionStatus(config.esploraUrl, txid);
              if (status?.txid !== txid || typeof status.status?.confirmed !== 'boolean')
                return { txid, candidate, outcome: 'failed' as const };
              return { txid, candidate, outcome: status.status.confirmed ? ('confirmed' as const) : ('pending' as const) };
            } catch (error) {
              if ((error as { status?: number })?.status !== 404) return { txid, candidate, outcome: 'failed' as const };
              const age = Date.now() - candidate.date;
              return {
                txid,
                candidate,
                outcome: !Number.isFinite(age) ? ('failed' as const) : age < 120_000 ? ('tooRecent' as const) : ('dropped' as const),
              };
            }
          }),
        );
        if (!isCurrent()) throw new Error('Wallet changed during transaction recovery.');
        if (!canRecover()) throw new Error('Wallet activity changed during transaction recovery.');
        for (const { txid, candidate, outcome } of checks) {
          if (outcome === 'confirmed') confirmedTransactions.add(txid);
          // A poll or another tab may have confirmed the transaction during this
          // lookup. Never overwrite its terminal record with an older 404.
          const latest = applyTransactionTombstones(get(), readStoredNotifications(state.vaultPubkey));
          if (
            confirmedTransactions.has(txid) ||
            latest.notifications.some((item) => item.metadata?.txid === txid && item.type === 'success')
          ) {
            result.confirmed += 1;
            continue;
          }
          if (latest.clearedTransactionIds.includes(txid)) continue;
          result[outcome] += 1;
          if (outcome !== 'dropped') continue;
          activePolls.get(txid)?.abort();
          activePolls.delete(txid);
          set({
            ...latest,
            pendingTransactions: latest.pendingTransactions.filter((tx) => tx.transactionId !== txid),
            pendingTransaction:
              latest.pendingTransaction?.transactionId === txid
                ? { ...latest.pendingTransaction, status: 'Dropped' }
                : latest.pendingTransaction,
            clearedTransactionIds: [...new Set([...latest.clearedTransactionIds, txid])],
          });
          get().addNotification({
            title: 'Transaction Dropped',
            type: 'dropped',
            description: 'Reported as dropped: the explorer could not find this transaction at least 2 minutes after submission.',
            metadata: { txid, actionType: candidate.actionType },
          });
        }
        return result;
      },

      // The 2nd argument allows to toggle:
      markAsViewed: (id: string, toggle = false) => {
        const state = get();

        if (!state.vaultPubkey) {
          return;
        }

        set({
          notifications: state.notifications.map((n) => (n.id === id ? { ...n, isViewed: toggle ? !n.isViewed : true } : n)),
        });
      },

      markAllAsViewed: () => {
        const state = get();

        if (!state.vaultPubkey) {
          return;
        }

        set({
          notifications: state.notifications.map((n) => ({ ...n, isViewed: true })),
        });
      },

      clearAll: () => {
        const state = get();

        if (!state.vaultPubkey) {
          return;
        }

        set({
          notifications: [],
          clearedNotificationIds: [...new Set([...state.clearedNotificationIds, ...state.notifications.map(notificationRecordKey)])],
        });
      },

      getUnviewedCount: () => {
        return get().notifications.filter(({ isViewed }) => !isViewed).length;
      },

      hasPendingVaultAction: () => hasPendingVaultActionInList(get().notifications),

      hasPendingSwapAction: () => hasPendingSwapActionInList(get().notifications),

      hasSwapUserActionRequired: () => hasSwapUserActionRequiredInList(get().notifications),

      /** Vault in-progress only; UNIT→USDC swap progress must not spin the toolbar. */
      hasInProgress: () => hasPendingVaultActionInList(get().notifications),

      setPendingTransaction: (
        tx: TransactionHistoryItem,
        { shouldPollStatus = true, shouldShowToast, onTxInMempool, onConfirmed } = {},
      ) => {
        if (shouldPollStatus && activePolls.has(tx.transactionId)) return;
        set((state) => ({
          pendingTransaction: tx,
          pendingTransactions: [...state.pendingTransactions.filter((item) => item.transactionId !== tx.transactionId), tx],
        }));

        const state = get();
        const { vaultPubkey, runeAddress } = state;

        if (shouldPollStatus && vaultPubkey) {
          const controller = new AbortController();
          activePolls.set(tx.transactionId, controller);
          const isCurrent = () => !controller.signal.aborted && currentVaultPubkey === vaultPubkey;
          const finish = (status: 'Completed' | 'Dropped') => {
            activePolls.delete(tx.transactionId);
            set((current) => ({
              pendingTransactions: current.pendingTransactions.filter((item) => item.transactionId !== tx.transactionId),
              pendingTransaction:
                current.pendingTransaction?.transactionId === tx.transactionId ? { ...tx, status } : current.pendingTransaction,
              clearedTransactionIds: [...new Set([...current.clearedTransactionIds, tx.transactionId])],
            }));
          };

          const onSuccess = () => {
            if (!isCurrent()) return;
            finish('Completed');

            // Update notification:
            const params = {
              title: getSuccessToastTitle(tx),
              type: 'success',
              description: getSuccessToastDescription(tx),
              metadata: { txid: tx.transactionId, actionType: tx.type },
            } as const;

            state.addNotification(params);

            if (shouldShowToast) {
              showToast(params);
            }

            if (tx.type === 'close') {
              scheduleVaultDataInvalidation(3000);
              scheduleWalletDataInvalidation(3000);
            }
          };

          const onTxNotFound = () => {
            if (!isCurrent() || confirmedTransactions.has(tx.transactionId)) return;
            const txAgeMs = Date.now() - new Date(tx.date).getTime();
            const threeHoursMs = 3 * 60 * 60 * 1000;
            if (!Number.isFinite(txAgeMs) || txAgeMs < threeHoursMs) return;

            finish('Dropped');

            const params = {
              title: `${getActionTitleByType(tx.type)} Dropped`,
              type: 'dropped',
              description: `Transaction was not found in the mempool after 3 hours`,
              metadata: { txid: tx.transactionId, actionType: tx.type },
            } as const;

            state.addNotification(params);
            controller.abort();

            if (shouldShowToast) {
              showToast(params);
            }
          };

          const shouldSync = tx.type !== 'swap';

          void pollPendingTxStatus({
            shouldSync,
            tx,
            vaultPubkey,
            runeAddress: runeAddress || '',
            signal: controller.signal,
            canReadVaultHistory: () => isCurrent() && get().allowVaultHistory,
            onSuccess,
            onTxInMempool: () => {
              if (isCurrent()) onTxInMempool?.();
            },
            onTxNotFound,
            onConfirmed: (confirmed) => {
              if (isCurrent()) onConfirmed?.(confirmed);
            },
          }).finally(() => {
            if (activePolls.get(tx.transactionId) === controller) activePolls.delete(tx.transactionId);
          });
        }
      },
      checkPendingNotifications: (historyTxs: VaultTransaction[]) => {
        // Update pending notifications if they are found in the vault history.
        const { notifications, pendingTransaction, updateNotification } = get();
        const pendingTransactionId = pendingTransaction?.transactionId;
        const vault_txid = pendingTransaction?.vault_txid;
        const latestHistoryAction = historyTxs.at(-1)?.action;

        notifications.forEach((notification) => {
          const txid = notification.metadata?.txid;
          if (txid && notification.type === 'progress' && txid !== pendingTransactionId && txid !== vault_txid) {
            const isCloseNotification = notification.metadata?.actionType === 'close';
            const foundInHistory = isCloseNotification
              ? latestHistoryAction === 'close'
              : historyTxs.some((tx) => tx.transaction_id === txid);
            if (foundInHistory) {
              updateNotification(notification.id, { type: 'success' });
            }
          }
        });
      },
    }),
    {
      name: 'notifications', // Base name (actual key is dynamic via custom storage)
      storage: createVaultStorage(),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<PersistedState>),
        allowVaultHistory: current.allowVaultHistory,
      }),
      partialize: (state): PersistedState => ({
        clearedNotificationIds: state.clearedNotificationIds,
        clearedTransactionIds: state.clearedTransactionIds,
        notifications: state.notifications,
        vaultPubkey: state.vaultPubkey,
        runeAddress: state.runeAddress,
        isInitialized: state.isInitialized,
        pendingTransaction: state.pendingTransaction,
        pendingTransactions: state.pendingTransactions,
      }),
    },
  ),
);

interface PollPendingTxStatusParams {
  shouldSync: boolean;
  tx: TransactionHistoryItem;
  vaultPubkey: string;
  runeAddress: string;
  signal?: AbortSignal;
  canReadVaultHistory: () => boolean;
  onSuccess: () => void;
  onTxInMempool?: () => void;
  onTxNotFound?: () => void;
  onConfirmed?: (tx: TransactionHistoryItem) => void;
}

const pollPendingTxStatus = async ({
  shouldSync,
  tx,
  vaultPubkey,
  runeAddress,
  signal,
  canReadVaultHistory,
  onSuccess,
  onTxInMempool,
  onTxNotFound,
  onConfirmed,
}: PollPendingTxStatusParams) => {
  const txid = tx.transactionId;
  const vault_txid = tx.vault_txid;
  const network = getFrontendNetwork();
  const config = getConfig(network);

  // If tx status is "In Progress" start polling tx status in mempool
  // once tx is confirmed in mempool, sync V/W/U data and utxos (depending on tx type)
  // and update tx status to "Confirmed" when all synced.
  if (vaultPubkey && config) {
    const { esploraUrl, validatorUrl } = config;

    try {
      await pollTxStatusAndSync({
        shouldSync,
        esploraUrl,
        validatorUrl,
        vaultPubkey,
        txids: [txid, vault_txid || ''],
        runeAddress: (tx.type === 'repay' && !tx.isFullRepay) || tx.type === 'borrow' ? runeAddress : '',
        expectClose: tx.type === 'close',
        expectOpen: tx.type === 'open',
        vaultRootTxid: tx.vault_root_txid,
        onTxInMempool,
        onTxNotFound,
        signal,
        canReadVaultHistory,
      });

      if (signal?.aborted) return;
      onSuccess();
      onConfirmed?.(tx);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
      }
    }
  }
};

// Helper functions to access store from non-React contexts
export const markAsViewed = (id: string | number | undefined, toggle = false): void => {
  if (id) {
    useNotificationStore.getState().markAsViewed(String(id), toggle);
  }
};

export const removeItem = (id: string | number | undefined): void => {
  id && useNotificationStore.getState().removeNotification(String(id));
};

export const addNotification = (notification: NotificationInput): string => {
  return useNotificationStore.getState().addNotification(notification);
};

export const getNotificationById = (id: string): NotificationItem | undefined => {
  return useNotificationStore.getState().notifications.find((n) => n.id === id);
};

// Keep visible state in sync with recovery performed by another tab.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (currentVaultPubkey && event.key === getStorageKey(currentVaultPubkey)) {
      const previous = useNotificationStore.getState();
      const finish = () => {
        let state = useNotificationStore.getState();
        if (state.vaultPubkey !== previous.vaultPubkey) return;
        // Near-simultaneous writes can each miss the other tab's transaction.
        // Retain local safety records and propagate their union back to disk.
        useNotificationStore.setState(applyTransactionTombstones(state, previous));
        state = useNotificationStore.getState();
        const pendingId = state.pendingTransaction?.transactionId;
        const transactions = [...state.pendingTransactions].sort(
          (a, b) => Number(a.transactionId === pendingId) - Number(b.transactionId === pendingId),
        );
        for (const tx of transactions) state.setPendingTransaction(tx, { shouldPollStatus: true });
      };
      const hydration = useNotificationStore.persist.rehydrate();
      if (hydration instanceof Promise) void hydration.then(finish);
      else finish();
    }
  });
}
